import React from "react";
import {
  Card,
  Elevation,
  Tag,
  Button,
  H1,
  H3,
  Text,
  InputGroup,
  Intent,
  HTMLSelect,
  NonIdealState,
  Spinner,
  ControlGroup,
} from "@blueprintjs/core";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-alpine-dark.css";
import { LicenseManager } from "ag-grid-enterprise";
import "ag-grid-enterprise";
import { inject, observer } from "mobx-react";
import { apiCallPost } from "../../../micro/fns/util";
import cogoToast from "cogo-toast";
import BranchService from "../../../micro/services/BranchService";
import AuditService from "../../../micro/services/AuditService";
import NewMultiSelector from "../../../micro/components/NewMultiSelector";

LicenseManager.setLicenseKey("");

class AnesthesiaPaymentMaster extends React.Component {
  state = {
    items: [],
    branches: [],

    // Loading states
    isLoading: true,
    isSaving: false,

    // Grid state
    gridApi: null,
    columnApi: null,
    quickFilterText: "",

    // New item modal
    showNewModal: false,
    newItem: {
      name: "",
      branches: [],
      rate: "",
      status: "Active",
    },

    // Edit modal
    isEditModalOpen: false,
    editingItem: null,
  };

  componentDidMount() {
    this.loadData();
  }

  onGridReady = (params) => {
    this.setState({ gridApi: params.api, columnApi: params.columnApi });
  };

  columnDefs = [
    {
      headerName: "Name",
      field: "name",
      width: 200,
      filter: "agTextColumnFilter",
      pinned: "left",
      editable: false,
    },
    {
      headerName: "Branch",
      field: "branches",
      width: 250,
      filter: "agTextColumnFilter",
      cellRenderer: (params) => {
        if (!params.value || params.value.length === 0) {
          return "No branches";
        }
        if (Array.isArray(params.value)) {
          return params.value.join(", ");
        }
        return String(params.value);
      },
      editable: false,
    },
    {
      headerName: "Rates (Rs.)",
      field: "rate",
      width: 140,
      filter: "agNumberColumnFilter",
      valueFormatter: (p) => (p.value || p.value === 0 ? `${p.value}` : ""),
      editable: false,
    },
    {
      headerName: "Status",
      field: "status",
      width: 140,
      filter: "agTextColumnFilter",
      editable: false,
    },
    {
      headerName: "Actions",
      field: "actions",
      width: 120,
      cellRenderer: (params) => {
        const container = document.createElement("div");
        const editBtn = document.createElement("button");
        const deleteBtn = document.createElement("button");

        editBtn.innerHTML = "✏️";
        editBtn.style.marginRight = "5px";
        editBtn.style.padding = "4px 8px";
        editBtn.style.border = "1px solid #137cbd";
        editBtn.style.backgroundColor = "#137cbd";
        editBtn.style.color = "white";
        editBtn.style.borderRadius = "3px";
        editBtn.style.cursor = "pointer";
        editBtn.onclick = () => this.editItem(params.data);

        deleteBtn.innerHTML = "🗑️";
        deleteBtn.style.padding = "4px 8px";
        deleteBtn.style.border = "1px solid #db3737";
        deleteBtn.style.backgroundColor = "#db3737";
        deleteBtn.style.color = "white";
        deleteBtn.style.borderRadius = "3px";
        deleteBtn.style.cursor = "pointer";
        deleteBtn.onclick = () => this.deleteItem(params.data._id);

        container.appendChild(editBtn);
        container.appendChild(deleteBtn);

        return container;
      },
      filter: false,
      sortable: false,
    },
  ];

  loadData = async () => {
    try {
      this.setState({ isLoading: true });
      cogoToast.loading("Loading anesthesia payment master...");
      await Promise.all([this.loadBranches(), this.loadItems()]);
      cogoToast.success("Data loaded successfully");
    } catch (err) {
      cogoToast.error("Failed to load data. Please try again.");
      console.error("Error loading anesthesia payment data:", err);
    } finally {
      this.setState({ isLoading: false });
    }
  };

  loadBranches = async () => {
    try {
      const branchNames = await BranchService.getBranchNames(this.props.authStore.prefix);
      this.setState({ branches: branchNames });
    } catch (err) {
      console.error("Failed to load branches:", err);
      this.setState({ branches: ["Main Branch"] });
    }
  };

  loadItems = async () => {
    try {
      const response = await apiCallPost(`/v3/${this.props.authStore.prefix}/neoSxAnesthesiaPaymentMaster/getAll`, {});
      this.setState({ items: response || [] });
    } catch (err) {
      console.error("Failed to load anesthesia payment master:", err);
      this.setState({ items: [] });
    }
  };

  editItem = (item) => {
    this.setState({ isEditModalOpen: true, editingItem: { ...item } });
  };

  closeEditModal = () => {
    this.setState({ isEditModalOpen: false, editingItem: null });
  };

  handleNewSubmit = () => {
    const { newItem } = this.state;
    if (!newItem.name.trim()) {
      cogoToast.error("Name is required");
      return;
    }
    if (!newItem.branches || newItem.branches.length === 0) {
      cogoToast.error("At least one branch must be selected");
      return;
    }
    if (newItem.rate === "" || isNaN(Number(newItem.rate)) || Number(newItem.rate) < 0) {
      cogoToast.error("Please enter a valid rate");
      return;
    }
    this.createNew();
  };

  createNew = async () => {
    this.setState({ isSaving: true });
    cogoToast.loading("Creating anesthesia payment record...");
    try {
      const payload = {
        name: this.state.newItem.name.trim(),
        branches: this.state.newItem.branches,
        rate: Number(this.state.newItem.rate),
        status: this.state.newItem.status,
      };
      const response = await apiCallPost(
        `/v3/${this.props.authStore.prefix}/neoSxAnesthesiaPaymentMaster/createNew`,
        payload
      );
      if (response && response._id) {
        const newRow = { ...response };
        this.setState((prev) => ({
          items: [newRow, ...(prev.items || [])],
          isSaving: false,
          showNewModal: false,
          newItem: { name: "", branches: [], rate: "", status: "Active" },
        }));

        try {
          await AuditService.logEntityCreate({
            entityType: "anesthesiaPayment",
            entityId: newRow._id,
            newData: newRow,
            authStore: this.props.authStore,
            description: `Created anesthesia payment "${newRow.name}"`,
          });
        } catch (auditErr) {
          console.error("Failed to create audit log:", auditErr);
        }

        cogoToast.success("Record created successfully");
      } else {
        throw new Error("Invalid create response");
      }
    } catch (err) {
      console.error("Create error:", err);
      cogoToast.error("Failed to create record");
      this.setState({ isSaving: false });
    }
  };

  updateFromModal = async () => {
    const { editingItem } = this.state;
    if (!editingItem.name.trim()) {
      cogoToast.error("Name is required");
      return;
    }
    if (!editingItem.branches || editingItem.branches.length === 0) {
      cogoToast.error("At least one branch must be selected");
      return;
    }
    if (editingItem.rate === "" || isNaN(Number(editingItem.rate)) || Number(editingItem.rate) < 0) {
      cogoToast.error("Please enter a valid rate");
      return;
    }

    this.setState({ isSaving: true });
    cogoToast.loading("Updating anesthesia payment record...");
    try {
      const updatePayload = {
        _id: editingItem._id,
        name: editingItem.name.trim(),
        branches: editingItem.branches,
        rate: Number(editingItem.rate),
        status: editingItem.status,
      };
      const oldData = this.state.items.find((x) => x._id === editingItem._id);
      const response = await apiCallPost(
        `/v3/${this.props.authStore.prefix}/neoSxAnesthesiaPaymentMaster/update`,
        updatePayload
      );
      if (response && response._id) {
        const updated = { ...response };
        this.setState((prev) => ({
          items: (prev.items || []).map((x) => (x._id === editingItem._id ? updated : x)),
          isSaving: false,
          isEditModalOpen: false,
          editingItem: null,
        }));

        try {
          await AuditService.logEntityUpdate({
            entityType: "anesthesiaPayment",
            entityId: editingItem._id,
            oldData,
            newData: updated,
            authStore: this.props.authStore,
            description: `Updated anesthesia payment "${updated.name}"`,
          });
        } catch (auditErr) {
          console.error("Failed to write update audit log:", auditErr);
        }

        cogoToast.success("Record updated successfully");
      } else {
        throw new Error("Invalid update response");
      }
    } catch (err) {
      console.error("Update error:", err);
      cogoToast.error("Failed to update record");
      this.setState({ isSaving: false });
    }
  };

  deleteItem = async (_id) => {
    this.setState({ isSaving: true });
    cogoToast.loading("Deleting record...");
    const deletedRow = this.state.items.find((x) => x._id === _id);
    try {
      const response = await apiCallPost(`/v3/${this.props.authStore.prefix}/neoSxAnesthesiaPaymentMaster/delete`, {
        _id,
      });
      if (response && response.success !== false) {
        this.setState((prev) => ({
          items: (prev.items || []).filter((x) => x._id !== _id),
          isSaving: false,
        }));

        try {
          if (deletedRow) {
            await AuditService.logEntityDelete({
              entityType: "anesthesiaPayment",
              entityId: _id,
              deletedData: deletedRow,
              authStore: this.props.authStore,
              description: `Deleted anesthesia payment "${deletedRow.name}"`,
            });
          }
        } catch (auditErr) {
          console.error("Failed to write delete audit log:", auditErr);
        }

        cogoToast.success("Record deleted successfully");
      } else {
        throw new Error("Delete failed");
      }
    } catch (err) {
      console.error("Delete error:", err);
      cogoToast.error("Failed to delete record");
      this.setState({ isSaving: false });
    }
  };

  renderHeader = () => {
    const { quickFilterText, isSaving, items, branches } = this.state;
    return (
      <div style={{ marginBottom: "var(--v3-spacing-lg)" }}>
        <H1 style={{ color: "var(--text-primary)", margin: 0 }}>Anesthesia Payment Master</H1>
        <Text
          style={{
            color: "var(--text-secondary)",
            margin: "var(--v3-spacing-xs) 0",
            fontSize: "var(--v3-font-size-md)",
            fontFamily: "var(--v3-font-family-sans)",
          }}
        >
          Manage anesthesia payment rates by branch and status
        </Text>

        <Card elevation={Elevation.TWO} style={{ padding: "var(--v3-spacing-lg)", marginTop: "var(--v3-spacing-lg)" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: "var(--v3-spacing-md)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "16px", flex: 1 }}>
              <div style={{ minWidth: "300px" }}>
                <InputGroup
                  leftIcon="search"
                  placeholder="Search name, branch, status..."
                  value={quickFilterText}
                  onChange={(e) => this.setState({ quickFilterText: e.target.value })}
                  disabled={isSaving}
                />
              </div>

              <div style={{ display: "flex", gap: "8px" }}>
                <Tag large intent="primary" icon="numerical">
                  {items.length} Records
                </Tag>
                <Tag large intent="success" icon="map-marker">
                  {branches.length} Branches
                </Tag>
              </div>
            </div>

            <div style={{ display: "flex", gap: "8px" }}>
              <Button
                icon="refresh"
                disabled={isSaving}
                onClick={() => {
                  this.loadBranches();
                  this.loadItems();
                }}
              />

              <Button
                icon="plus"
                intent="primary"
                disabled={isSaving}
                onClick={() => this.setState({ showNewModal: true })}
              >
                New Record
              </Button>
            </div>
          </div>
        </Card>
      </div>
    );
  };

  renderTable = () => {
    const { items, quickFilterText, isLoading } = this.state;
    if (!items.length) {
      return (
        <Card elevation={Elevation.TWO} style={{ padding: "20px" }}>
          <div style={{ textAlign: "center", padding: "40px" }}>
            <H3 style={{ color: "var(--text-primary)", marginBottom: "20px" }}>
              {quickFilterText ? "No matching records found" : "No records configured"}
            </H3>
            <Text
              style={{
                color: "var(--text-secondary)",
                marginBottom: "var(--v3-spacing-xl)",
                fontSize: "var(--v3-font-size-md)",
                fontFamily: "var(--v3-font-family-sans)",
              }}
            >
              {quickFilterText
                ? "Try adjusting your search terms or clear the search to view all records."
                : "Get started by creating your first anesthesia payment record."}
            </Text>
            {quickFilterText ? (
              <Button icon="cross" intent="primary" onClick={() => this.setState({ quickFilterText: "" })}>
                Clear Search
              </Button>
            ) : (
              <Button
                icon="plus"
                intent="primary"
                large
                onClick={() => this.setState({ showNewModal: true })}
                loading={this.state.isSaving}
              >
                Create First Record
              </Button>
            )}
          </div>
        </Card>
      );
    }

    return (
      <Card elevation={Elevation.TWO} style={{ padding: "20px" }}>
        <div className="ag-theme-alpine-dark" style={{ height: "500px", width: "100%" }}>
          <AgGridReact
            columnDefs={this.columnDefs}
            rowData={items}
            onGridReady={this.onGridReady}
            quickFilterText={quickFilterText}
            defaultColDef={{
              sortable: true,
              filter: true,
              resizable: true,
            }}
            rowSelection="single"
            enableRangeSelection={true}
            enableCharts={true}
            sideBar={true}
            statusBar={{
              statusPanels: [
                {
                  statusPanel: "agTotalAndFilteredRowCountComponent",
                  align: "left",
                },
              ],
            }}
            loading={isLoading}
          />
        </div>
      </Card>
    );
  };

  render() {
    if (this.state.isLoading) {
      return (
        <div
          className="bp3-dark"
          style={{
            padding: "20px",
            backgroundColor: "var(--bg-primary)",
            color: "var(--text-primary)",
            minHeight: "100vh",
          }}
        >
          <NonIdealState
            icon={<Spinner size={50} />}
            title="Loading Anesthesia Payment Master"
            description="Please wait while we load anesthesia payment data..."
          />
        </div>
      );
    }

    return (
      <div
        className="bp3-dark"
        style={{
          padding: "20px",
          backgroundColor: "var(--bg-primary)",
          color: "var(--text-primary)",
          minHeight: "100vh",
        }}
      >
        {this.renderHeader()}
        {this.renderTable()}
        {this.renderNewModal()}
        {this.renderEditModal()}
      </div>
    );
  }

  renderEditModal = () => {
    const { isEditModalOpen, editingItem, branches, isSaving } = this.state;
    if (!isEditModalOpen || !editingItem) return null;
    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.7)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1000,
        }}
        onClick={this.closeEditModal}
      >
        <div
          style={{
            backgroundColor: "var(--bg-surface)",
            borderRadius: "8px",
            padding: "30px",
            maxWidth: "800px",
            width: "90%",
            maxHeight: "90vh",
            overflowY: "auto",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.3)",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={{ marginBottom: "20px" }}>
            <H3 style={{ color: "var(--text-primary)", margin: 0 }}>Edit Record</H3>
            <p style={{ color: "var(--text-secondary)", margin: "5px 0" }}>Update the anesthesia payment details.</p>
          </div>

          <div style={{ display: "flex", flexWrap: "wrap", gap: "15px", marginBottom: "20px" }}>
            <ControlGroup>
              <Tag large minimal>
                Name *
              </Tag>
              <InputGroup
                placeholder="Enter name..."
                value={editingItem.name}
                onChange={(e) => this.setState({ editingItem: { ...editingItem, name: e.target.value } })}
                disabled={isSaving}
                intent={!editingItem.name.trim() ? Intent.DANGER : Intent.NONE}
                style={{ width: "300px" }}
              />
            </ControlGroup>

            <div style={{ width: "100%", marginTop: "10px" }}>
              <ControlGroup>
                <Tag large minimal>
                  Branches *
                </Tag>
                <div style={{ flex: 1, minWidth: "400px" }}>
                  <NewMultiSelector
                    items={branches}
                    selectedItems={editingItem.branches}
                    onChange={(selectedBranches) =>
                      this.setState({ editingItem: { ...editingItem, branches: selectedBranches } })
                    }
                    placeholder="Select branches..."
                    label="Branches"
                    showLabel={false}
                    maxDisplayItems={3}
                    sortItems={true}
                    emptyMessage="No branches available"
                    disabled={isSaving}
                  />
                </div>
              </ControlGroup>
            </div>

            <ControlGroup>
              <Tag large minimal>
                Rates (Rs.) *
              </Tag>
              <InputGroup
                placeholder="Enter rate"
                type="number"
                min={0}
                step="1"
                value={editingItem.rate}
                onChange={(e) => {
                  const raw = e.target.value;
                  const next = raw === "" ? "" : Math.max(0, Number(raw));
                  this.setState({ editingItem: { ...editingItem, rate: next } });
                }}
                disabled={isSaving}
                intent={
                  editingItem.rate === "" || isNaN(Number(editingItem.rate)) || Number(editingItem.rate) < 0
                    ? Intent.DANGER
                    : Intent.NONE
                }
                style={{ width: "200px" }}
              />
            </ControlGroup>

            <ControlGroup>
              <Tag large minimal>
                Status *
              </Tag>
              <HTMLSelect
                options={[
                  { label: "Active", value: "Active" },
                  { label: "Inactive", value: "Inactive" },
                ]}
                value={editingItem.status}
                onChange={(e) => this.setState({ editingItem: { ...editingItem, status: e.target.value } })}
                disabled={isSaving}
              />
            </ControlGroup>
          </div>

          <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
            <Button large intent="none" icon="cross" onClick={this.closeEditModal}>
              Cancel
            </Button>
            <Button
              large
              intent="primary"
              icon="tick"
              onClick={this.updateFromModal}
              loading={isSaving}
              disabled={
                isSaving ||
                !editingItem.name.trim() ||
                !editingItem.branches.length ||
                editingItem.rate === "" ||
                isNaN(Number(editingItem.rate)) ||
                !editingItem.status.trim()
              }
            >
              Update
            </Button>
          </div>
        </div>
      </div>
    );
  };

  renderNewModal = () => {
    const { showNewModal, newItem, branches, isSaving } = this.state;
    if (!showNewModal) return null;
    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.7)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1000,
        }}
        onClick={() =>
          this.setState({ showNewModal: false, newItem: { name: "", branches: [], rate: "", status: "Active" } })
        }
      >
        <div
          style={{
            backgroundColor: "var(--bg-surface)",
            borderRadius: "8px",
            padding: "30px",
            maxWidth: "800px",
            width: "90%",
            maxHeight: "90vh",
            overflowY: "auto",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.3)",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={{ marginBottom: "20px" }}>
            <H3 style={{ color: "var(--text-primary)", margin: 0 }}>Create New Record</H3>
            <p style={{ color: "var(--text-secondary)", margin: "5px 0" }}>
              Enter the anesthesia payment details and assign branches.
            </p>
          </div>

          <div style={{ display: "flex", flexWrap: "wrap", gap: "15px", marginBottom: "20px" }}>
            <ControlGroup>
              <Tag large minimal>
                Name *
              </Tag>
              <InputGroup
                placeholder="Enter name..."
                value={newItem.name}
                onChange={(e) => this.setState({ newItem: { ...newItem, name: e.target.value } })}
                disabled={isSaving}
                intent={!newItem.name.trim() ? Intent.DANGER : Intent.NONE}
                style={{ width: "300px" }}
              />
            </ControlGroup>

            <div style={{ width: "100%", marginTop: "10px" }}>
              <ControlGroup>
                <Tag large minimal>
                  Branches *
                </Tag>
                <div style={{ flex: 1, minWidth: "400px" }}>
                  <NewMultiSelector
                    items={branches}
                    selectedItems={newItem.branches}
                    onChange={(selectedBranches) =>
                      this.setState({ newItem: { ...newItem, branches: selectedBranches } })
                    }
                    placeholder="Select branches..."
                    label="Branches"
                    showLabel={false}
                    maxDisplayItems={3}
                    sortItems={true}
                    emptyMessage="No branches available"
                    disabled={isSaving}
                  />
                </div>
              </ControlGroup>
            </div>

            <ControlGroup>
              <Tag large minimal>
                Rates (Rs.) *
              </Tag>
              <InputGroup
                placeholder="Enter rate"
                type="number"
                min={0}
                step="1"
                value={newItem.rate}
                onChange={(e) => {
                  const raw = e.target.value;
                  const next = raw === "" ? "" : Math.max(0, Number(raw));
                  this.setState({ newItem: { ...newItem, rate: next } });
                }}
                disabled={isSaving}
                intent={
                  newItem.rate === "" || isNaN(Number(newItem.rate)) || Number(newItem.rate) < 0
                    ? Intent.DANGER
                    : Intent.NONE
                }
                style={{ width: "200px" }}
              />
            </ControlGroup>

            <ControlGroup>
              <Tag large minimal>
                Status *
              </Tag>
              <HTMLSelect
                options={[
                  { label: "Active", value: "Active" },
                  { label: "Inactive", value: "Inactive" },
                ]}
                value={newItem.status}
                onChange={(e) => this.setState({ newItem: { ...newItem, status: e.target.value } })}
                disabled={isSaving}
                fill={false}
              />
            </ControlGroup>
          </div>

          <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
            <Button
              large
              intent="none"
              icon="cross"
              onClick={() =>
                this.setState({
                  showNewModal: false,
                  newItem: { name: "", branches: [], rate: "", status: "Active" },
                })
              }
            >
              Cancel
            </Button>
            <Button
              large
              intent="primary"
              icon="tick"
              onClick={this.handleNewSubmit}
              loading={isSaving}
              disabled={
                isSaving ||
                !newItem.name.trim() ||
                !newItem.branches.length ||
                newItem.rate === "" ||
                isNaN(Number(newItem.rate)) ||
                !newItem.status.trim()
              }
            >
              Create
            </Button>
          </div>
        </div>
      </div>
    );
  };
}

export default inject("authStore")(observer(AnesthesiaPaymentMaster));
